/** @jsxImportSource @emotion/react */
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Breadcrumb, Card, Typography, Button, Space, Table, Tag } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { css } from '@emotion/react';
import { commonStyles } from '../../styles/commonStyles';
import MainLayout from '../../components/common/MainLayout';
import { useGetPropertiesQuery } from '../../api/plantingEventsApi';
import type { Property } from '../../types/plantingEvents';
import { useSave } from '../../contexts/WorldContext';

const { Title, Text } = Typography;

const FarmPropertiesPage: React.FC = () => {
  const { saveId } = useParams<{ saveId: string }>();
  const save = useSave();
  const saveTitle = save.farmName || save.id.slice(0, 8);

  const {
    data: attributes = [],
    isLoading,
    isError,
  } = useGetPropertiesQuery('planting');

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: Property) => (
        <div>
          <Text strong>{text}</Text>
          <br />
          <Text type="secondary" css={css({ fontSize: '0.85em' })}>
            Код: {record.code}
          </Text>
        </div>
      ),
    },
    {
      title: 'Тип данных',
      dataIndex: 'dataType',
      key: 'dataType',
      width: 120,
      render: (dataType: string) => {
        const colorMap: Record<string, string> = {
          integer: 'blue',
          float: 'cyan',
          text: 'green',
          boolean: 'purple',
        };
        return <Tag color={colorMap[dataType] || 'default'}>{dataType}</Tag>;
      },
    },
    {
      title: 'Опции',
      dataIndex: 'options',
      key: 'options',
      render: (options: string[]) =>
        options && options.length > 0 ? (
          <div>
            {options.slice(0, 3).map((opt, idx) => (
              <Tag key={idx} css={css({ marginBottom: 4 })}>
                {opt}
              </Tag>
            ))}
            {options.length > 3 && <Text type="secondary">+{options.length - 3}</Text>}
          </div>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Единица',
      dataIndex: 'unitId',
      key: 'unitId',
      width: 100,
      render: (unitId: number | null) => (unitId ? `Ед. ${unitId}` : '—'),
    },
  ];

  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb
          items={[
            { title: <Link to="/">Главная</Link> },
            { title: <Link to={`/saves/${saveId}`}>{saveTitle}</Link> },
            { title: 'Свойства культур' },
          ]}
        />
      }
    >
      <Title level={2}>Свойства культур</Title>

      <Card>
        <Space direction="vertical" size="large" css={commonStyles.fullWidth}>
          <div>
            <Title level={4}>Измеряемые параметры (Properties)</Title>
            <Text type="secondary">
              Это <strong>динамические атрибуты</strong> для записи измерений конкретных экземпляров растений 
              (высота, здоровье, результаты осмотра и т.д.). В отличие от <strong>постоянных характеристик культуры</strong> 
              (цвет хвои, форма кроны), эти параметры меняются со временем и записываются через события.
            </Text>
          </div>

          {isLoading && <div>Загрузка...</div>}
          {isError && <div>Ошибка загрузки атрибутов</div>}

          {!isLoading && !isError && (
            <Table
              dataSource={attributes}
              columns={columns}
              rowKey="id"
              pagination={false}
              bordered
            />
          )}

          <div>
            <Title level={5}>Как создать событие?</Title>
            <ol css={css({ paddingLeft: 20 })}>
              <li>Перейдите на страницу конкретной посадки</li>
              <li>Нажмите кнопку "Создать событие"</li>
              <li>Выберите тип события (инспекция, измерение и т.д.)</li>
              <li>Заполните значения нужных атрибутов</li>
              <li>Сохраните событие</li>
            </ol>
          </div>

          <Space>
            <Button type="default" onClick={() => window.history.back()}>
              Назад
            </Button>
            <Button type="primary" icon={<PlusOutlined />} disabled>
              Массовое создание событий (в разработке)
            </Button>
          </Space>
        </Space>
      </Card>
    </MainLayout>
  );
};

export default FarmPropertiesPage;
